import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

const tips = [
  {
    number: 1,
    icon: 'ri-user-voice-line',
    title: 'Defina os canais antes de tudo',
    description: 'Antes de qualquer decisão logística, defina quantos canais simultâneos seu evento precisa. Palestra principal + tradução? Ou múltiplas trilhas de conteúdo? Isso determina o equipamento necessário e o orçamento.',
    highlight: 'Planeje os canais primeiro, o resto se encaixa depois.',
  },
  {
    number: 2,
    icon: 'ri-map-pin-line',
    title: 'Teste o alcance do sinal no local',
    description: 'A radiofrequência dos fones alcança até 100m em campo aberto, mas paredes grossas e estruturas metálicas podem interferir. Agende uma visita técnica antes do evento para mapear zonas de sombra e posicionar antenas auxiliares se necessário.',
    highlight: 'Uma visita técnica evita 90% dos problemas no dia.',
  },
  {
    number: 3,
    icon: 'ri-psychotherapy-line',
    title: 'Prepare os palestrantes para o silêncio',
    description: 'Palestrantes acostumados com caixas de som podem estranhar o ambiente silencioso. Explique que o microfone funciona exatamente igual — a diferença é que o retorno do áudio vai direto para os fones. Faça um soundcheck de 10 minutos antes do evento.',
    highlight: '10 minutos de soundcheck eliminam qualquer desconforto.',
  },
  {
    number: 4,
    icon: 'ri-hand-sanitizer-line',
    title: 'Higienização visível transmite confiança',
    description: 'Posicione a estação de higienização dos fones em local visível na entrada. Os participantes verem os fones sendo limpos com álcool isopropílico 70% gera tranquilidade imediata. Para eventos acima de 300 pessoas, use capas descartáveis individuais.',
    highlight: 'Higienização à vista = confiança instantânea.',
  },
  {
    number: 5,
    icon: 'ri-group-line',
    title: 'Tenha uma equipe de apoio nos acessos',
    description: 'Posicione 1 pessoa da equipe a cada 100 participantes para ajudar com os fones na entrada. Muita gente nunca usou o equipamento e 30 segundos de orientação individual fazem toda a diferença na experiência.',
    highlight: '1 staff a cada 100 pessoas garante entrada fluida.',
  },
  {
    number: 6,
    icon: 'ri-signal-wifi-line',
    title: 'Sempre leve fones de backup',
    description: 'A regra de ouro é 10% de fones extras além do número contratado. Bateria descarregada, queda acidental, defeito raro — com backup, o problema se resolve em segundos e o participante nem percebe.',
    highlight: '10% de fones extras = zero estresse no dia.',
  },
  {
    number: 7,
    icon: 'ri-megaphone-line',
    title: 'Comunique o formato antes do evento',
    description: 'Muitos participantes nunca viveram uma palestra silenciosa. Envie um e-mail ou WhatsApp explicando como funciona 2 dias antes. Inclua: "Você receberá um fone na entrada, escolherá o canal e ajustará seu próprio volume."',
    highlight: 'Um aviso prévio reduz a ansiedade e multiplica o engajamento.',
  },
  {
    number: 8,
    icon: 'ri-bar-chart-box-line',
    title: 'Meça o engajamento por canal',
    description: 'Peça ao fornecedor um relatório pós-evento com a distribuição de audiência por canal ao longo do tempo. Esses dados revelam quais temas e palestrantes geraram mais retenção — ouro puro para planejar a próxima edição.',
    highlight: 'Dados de audiência por canal = planejamento inteligente.',
  },
];

export default function TipsSection() {
  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: tipsRef, isVisible: tipsVisible } = useScrollAnimation({ threshold: 0.05 });

  return (
    <section className="py-20 md:py-32 bg-white">
      <div className="max-w-6xl mx-auto px-4 md:px-6">
        {/* Header */}
        <div ref={headerRef} className="text-center mb-16">
          <span className="font-body text-xs font-semibold uppercase tracking-[0.2em] text-primary mb-4 block">
            Dicas de Especialista
          </span>
          <h2
            className={`font-heading font-bold text-4xl md:text-6xl text-secondary mb-6 transition-all duration-700 ${headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          >
            8 dicas práticas
          </h2>
          <p
            className={`font-body text-lg text-gray-500 max-w-xl mx-auto transition-all duration-700 delay-150 ${headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          >
            Aprendizado de mais de 500 eventos — resumido para o seu sucesso
          </p>
        </div>

        {/* Tips grid */}
        <div ref={tipsRef} className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
          {tips.map((tip, i) => (
            <div
              key={i}
              className={`group flex gap-5 p-5 md:p-7 bg-gray-50 border border-gray-100 rounded-2xl hover:border-primary/20 transition-all duration-500 ${tipsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              style={{ transitionDelay: `${i * 60}ms` }}
            >
              {/* Number circle */}
              <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center rounded-full bg-primary text-secondary font-heading font-extrabold text-lg">
                {tip.number}
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <h3 className="font-heading font-bold text-base text-secondary mb-2 group-hover:text-primary transition-colors">
                  {tip.title}
                </h3>
                <p className="font-body text-sm text-gray-600 leading-relaxed mb-3">
                  {tip.description}
                </p>
                <div className="inline-flex items-center gap-2 bg-primary/8 rounded-full px-3 py-1.5">
                  <i className="ri-flashlight-line text-primary text-xs" />
                  <span className="font-body text-xs text-primary font-medium">{tip.highlight}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}